import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  hideTransactionButtonSelector,
  initiateProcessing,
  resetPaymentMethod,
  selectAccountCreateInProgress,
  selectAmount,
  selectCurrencySymbol,
  selectFormReady,
  selectInputErrorMap,
  selectPaymentFormActivated,
  selectPaymentInputs,
  selectPaymentMethod,
  selectPaymentMethodSelectedManually,
  selectPaymentMethodsLoading,
  selectProcessingLoading,
  selectRegistrationRequired,
  setAmount,
  setBonusCode,
  setInputErrorMap,
  setPaymentInputs,
  setTermsAgreementCheckbox,
} from '@cashier/cashier-data-access';
import {
  FieldsByTypeInterface,
  InputTypes,
  PaymentMethodConfiguration,
  PaymentMethodInput,
  PaymentTypes,
} from '@cashier/cashier-model';
import { eventBus, EventTypes } from '@cashier/cashier-shared';

import {
  AMOUNT_FIELD_NAME,
  CREDITCARD_CATEGORY_NAME,
  FIAT_AMOUNT,
} from '../constants';
import {
  FinteqHubService,
  getCookie,
  handleAmountField,
  handleSelectField,
  QaToolKitOptions,
} from '../helpers';
import { selectActiveInputs, selectFieldsByType } from '../selectors';
import { useAmountValidationErrors } from './useAmountValidationErrors';
import { useCashierConfiguration } from './useCashierConfiguration';

interface UsePaymentForm {
  total: number;
  areInputsValid: boolean;
  customInputChange: (
    name: string,
    value: string,
    isValid: boolean,
    isFocused: boolean,
    isEmpty?: boolean,
  ) => void;
  updateAmount: (
    name: string,
    value: string,
    isValid?: boolean,
    isFocused?: boolean,
  ) => void;
  favoriteAmountClickHandler: (amount: string | number) => void;
  favoriteAmountKeyboardHandler: (
    event: SyntheticEvent,
    amount: number,
  ) => void;
  agreeCheckboxClick: () => void;
  agree: boolean;
  paymentInputs: Record<string, PaymentMethodInput>;
  isCheckboxVisible: boolean;
  isButtonDisabled: boolean;
  areFavoriteAmountsVisible: boolean;
  isAccountCreateInProgress: boolean;
  isVisible: boolean;
  resetValues: boolean;
  isActivated: boolean;
  isLoaderActive: boolean;
  arePaymentMethodsLoading: boolean;
  isFormReady: boolean;
  userAmount: string;
  paymentMethod: PaymentMethodConfiguration;
  activeType: PaymentTypes;
  activeInputs: PaymentMethodInput[];
  amountValidationErrors: string[];
  inputErrorMap: Record<string, string[]>;
  resetPaymentMethod: () => void;
  initiateProcessing: () => void;
  currency: string;
  hideTransactionButton: boolean;
  currentFields: FieldsByTypeInterface[];
  isRegistrationRequired: boolean;
}

export const usePaymentForm = (): UsePaymentForm => {
  const isProcessingLoading = useSelector(selectProcessingLoading);
  const userAmount = useSelector(selectAmount);
  const inputErrorMap = useSelector(selectInputErrorMap);
  const paymentMethod = useSelector(selectPaymentMethod);
  const activeInputs = useSelector(selectActiveInputs);
  const paymentInputs = useSelector(selectPaymentInputs);
  const isAccountCreateInProgress = useSelector(selectAccountCreateInProgress);
  const isPaymentFormActivated = useSelector(selectPaymentFormActivated);
  const isFinalScreenActivated = useSelector(selectPaymentFormActivated);
  const isPaymentMethodSelectedManually = useSelector(
    selectPaymentMethodSelectedManually,
  );
  const arePaymentMethodsLoading = useSelector(selectPaymentMethodsLoading);
  const isFormReady = useSelector(selectFormReady);
  const currency = useSelector(selectCurrencySymbol);
  const hideTransactionButton = useSelector(hideTransactionButtonSelector);
  const currentFields = useSelector(selectFieldsByType);
  const isRegistrationRequired = useSelector(selectRegistrationRequired);

  const amountValidationErrors = useAmountValidationErrors();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isValidationDisabled =
    getCookie(QaToolKitOptions.disableValidation) === 'true';

  const inputErrorMsgMap = {
    [InputTypes.Cvv]: t('cvvValidationFailed.message'),
    [InputTypes.DateTime]: t('dateFormatValidationFailed.message'),
    [InputTypes.Email]: t('emailValidationFailed.message'),
    [InputTypes.CreditCardVisa]: t('cardNumberValidationFailed.message'),
    [InputTypes.CreditCardMastercard]: t('cardNumberValidationFailed.message'),
    [InputTypes.CreditCardNumber]: t('cardNumberValidationFailed.message'),
    [InputTypes.IdentificationNumber]: t('rucIdValidationFailed.message'),
  };

  const noCheckboxMethodIds = useMemo(() => {
    return [
      'payment-iq-cryptocurrency-quickbit',
      'payment-iq-webredirect-flykk',
      'SwitchPaymentCard',
    ];
  }, []);

  const [total, setTotal] = useState<number>(0);
  const [agree, setAgree] = useState<boolean>(false);
  const [areInputsValid, setInputsValid] = useState<boolean>(false);
  const [stateValues, setStateValues] = useState({});
  const { amount: platformAmount, activeType } = useCashierConfiguration();

  const amount = userAmount || platformAmount;

  const { id, hideAmount, categories, ruleParams } = paymentMethod;
  const { amounts, min, max } = ruleParams || {};

  useEffect(() => {
    setStateValues({});
    setAgree(false);
  }, [paymentMethod]);

  useEffect(() => {
    return eventBus.on(EventTypes.ON_BONUS_SELECTED, (event) => {
      dispatch(setBonusCode(event.payload || ''));
    });
  }, [dispatch]);

  useEffect(() => {
    if (activeInputs) {
      const valuesSet = activeInputs.reduce((acc, properties) => {
        if (properties.type === InputTypes.Select) {
          acc[properties.name] = handleSelectField(properties);
        } else if (
          properties.name === AMOUNT_FIELD_NAME ||
          properties.name === FIAT_AMOUNT
        ) {
          acc[properties.name] = handleAmountField(
            paymentInputs,
            amount,
            min,
            max,
            properties,
          );
        } else {
          acc[properties.name] = FinteqHubService.enrichInputsWithSessionItems(
            paymentMethod.preprocessor,
            properties,
          );
        }

        return acc;
      }, {});
      setStateValues(valuesSet);
    }
  }, [activeInputs]);

  useEffect(() => {
    dispatch(setPaymentInputs(stateValues));
  }, [stateValues, dispatch]);

  useEffect(() => {
    const x = Object.values(stateValues).find(
      ({ isValid, required, name, type, value }) => {
        if (
          !required ||
          name === AMOUNT_FIELD_NAME ||
          name === 'accountId' ||
          (type === InputTypes.Hidden && Boolean(String(value)))
        )
          return false;

        return !isValid;
      },
    );

    setInputsValid(!x);
  }, [stateValues]);

  useEffect(() => {
    if (!stateValues[AMOUNT_FIELD_NAME]) return;

    const floatAmount = parseFloat(stateValues[AMOUNT_FIELD_NAME].value);
    floatAmount > 0 ? setTotal(floatAmount) : setTotal(0);
  }, [stateValues]);

  const customInputChange = (
    name,
    value,
    isValid,
    isFocused = true,
    isEmpty,
  ) => {
    const paymentMethodInput = activeInputs.find(
      ({ name: inputName }) => inputName === name,
    );

    if (paymentMethodInput) {
      if (inputErrorMap[name]) {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const { [name]: key, ...rest } = inputErrorMap;
        dispatch(setInputErrorMap(rest || {}));
      }

      // add function to modify mask depending on value
      setStateValues((old) => ({
        ...old,
        [name]: {
          ...paymentMethodInput,
          value: value,
          isValid: isValidationDisabled ? true : isValid,
          errorMsg: !isEmpty && inputErrorMsgMap[paymentMethodInput.type],
          isFocused,
        },
      }));
    }
  };

  const updateAmount = (
    name: string,
    newAmount: string,
    isValid?: boolean,
    isFocused?: boolean,
  ): void => {
    const paymentMethodInput = activeInputs.find(
      ({ name: inputName }) => inputName === name,
    );

    setStateValues((old) => ({
      ...old,
      [name]: {
        ...paymentMethodInput,
        value: newAmount.toString(),
        isValid: isValidationDisabled ? true : isValid,
        errorMsg: null,
        isFocused,
      },
    }));

    dispatch(setAmount(newAmount.toString()));
  };

  const favoriteAmountClickHandler = useCallback(
    (amount: number) => {
      if (!isProcessingLoading) {
        dispatch(setAmount(amount.toString()));
      }
    },
    [isProcessingLoading],
  );

  const favoriteAmountKeyboardHandler = (event, amount: number) => {
    if (event.keyCode === 13) {
      dispatch(setAmount(amount.toString()));
    }
  };

  const toggleAgree = () => {
    setAgree(!agree);
    dispatch(
      setTermsAgreementCheckbox({
        isCheckboxVisible: true,
        isChecked: !agree,
      }),
    );
  };

  const isCheckboxVisible = useMemo((): boolean => {
    // Show checkbox only if alternativeMessage exists OR agreement message and link exists
    const requiredTranslationsExists =
      !!(t('agreement.message', '') && t('agreement.link', '')) ||
      !!t('agreement.alternativeMessage', '');

    return (
      !hideAmount &&
      categories.some(
        (category) => category.name === CREDITCARD_CATEGORY_NAME,
      ) &&
      !noCheckboxMethodIds.includes(id) &&
      activeType === PaymentTypes.Deposit &&
      requiredTranslationsExists
    );
  }, [id, hideAmount, activeType, categories]);

  useEffect(() => {
    if (isCheckboxVisible) {
      dispatch(
        setTermsAgreementCheckbox({
          isCheckboxVisible: true,
          isChecked: false,
        }),
      );
    }
  }, [isCheckboxVisible, dispatch]);

  const isButtonDisabled = useMemo(() => {
    const { id, hideAmount } = paymentMethod;
    const { amount: amountField = null as PaymentMethodInput } = paymentInputs;

    if (getCookie(QaToolKitOptions.disableValidation) === 'true') return false;
    if (hideAmount && areInputsValid) return false;
    if (null !== amountField && !amount) return true;

    return (
      !id ||
      isProcessingLoading ||
      !areInputsValid ||
      !!amountValidationErrors.length ||
      (isCheckboxVisible && !agree)
    );
  }, [
    amount,
    paymentMethod,
    paymentInputs,
    isProcessingLoading,
    areInputsValid,
    amountValidationErrors,
    noCheckboxMethodIds,
    agree,
    activeType,
    categories,
  ]);

  const areFavoriteAmountsVisible = useMemo((): boolean => {
    return (
      activeType === PaymentTypes.Deposit &&
      amounts.length > 0 &&
      !hideAmount &&
      !platformAmount
    );
  }, [activeType, amounts, hideAmount, platformAmount]);

  const initiateProcessingHandler = useCallback(() => {
    dispatch(initiateProcessing());
  }, [dispatch]);

  useEffect(() => {
    return eventBus.on(
      EventTypes.ON_PAYMENT_DATA_PRE_PROCESSED,
      initiateProcessingHandler,
    );
  }, [initiateProcessingHandler]);

  const resetPaymentMethodHandler = useCallback(() => {
    dispatch(resetPaymentMethod());
  }, [dispatch]);

  return {
    total,
    areInputsValid,
    customInputChange,
    updateAmount,
    favoriteAmountClickHandler,
    favoriteAmountKeyboardHandler,
    agreeCheckboxClick: toggleAgree,
    agree,
    paymentInputs,
    isCheckboxVisible,
    isButtonDisabled,
    areFavoriteAmountsVisible,
    isAccountCreateInProgress,
    isVisible: isPaymentFormActivated,
    resetValues: isFinalScreenActivated,
    isActivated: isPaymentMethodSelectedManually,
    isLoaderActive: isProcessingLoading,
    arePaymentMethodsLoading,
    isFormReady,
    userAmount,
    paymentMethod,
    activeType,
    activeInputs,
    amountValidationErrors,
    inputErrorMap,
    resetPaymentMethod: resetPaymentMethodHandler,
    initiateProcessing: initiateProcessingHandler,
    currency,
    hideTransactionButton,
    currentFields,
    isRegistrationRequired,
  };
};
